/**
 * Newsletter-/Mail-Panels als wiederverwendbare Bausteine.
 *
 * Werden in zwei Bereichen eingehängt:
 *   Kundenportal → Tab „Newsletter"     → MarketingSenderPanel + CampaignsPanel
 *   Einstellungen → Tab „System-Mails"  → SystemMailPanel
 *   Einstellungen → Tab „Mail-Verlauf"  → MailHistoryPanel
 *
 * Alle Panels kommunizieren nur über die vorhandenen Backend-Endpunkte
 * /api/admin/newsletter/*. Der eigenständige /newsletter-Screen fällt
 * dadurch weg — es gibt genau eine Wahrheit pro Feature.
 */
import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Mail, Plus, Pencil, Trash2, Save, Send, Eye, Calendar,
  RefreshCw, ShieldCheck, Search, Zap,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../ui/dialog";
import { NewsletterEditor } from "../NewsletterEditor";
import { AgeSelect, ComplianceWarning, AGE_OPTIONS } from "../AgeAndCompliance";

// ==========================================================================
// Settings-Query-Helpers (geteilt zwischen SystemMailPanel + MarketingSender)
// ==========================================================================

function useSettings() {
  return useQuery({
    queryKey: ["nl-settings"],
    queryFn: async () => (await api.get("/admin/newsletter/settings")).data,
  });
}

function useSaveSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      (await api.put("/admin/newsletter/settings", body)).data,
    onSuccess: () => {
      toast.success("Einstellungen gespeichert");
      qc.invalidateQueries({ queryKey: ["nl-settings"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
}

function FieldRow({ label, tid, value, onChange, placeholder }) {
  return (
    <div className="grid sm:grid-cols-3 gap-2 mb-2 items-center">
      <Label className="text-xs uppercase tracking-wider text-zinc-400 sm:col-span-1">{label}</Label>
      <Input
        data-testid={tid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        className="h-10 bg-zinc-950 border-zinc-800 rounded-sm sm:col-span-2"
      />
    </div>
  );
}

// ==========================================================================
// SystemMailPanel — technische System-Mail-Absender + Testmail
// ==========================================================================
export function SystemMailPanel() {
  const q = useSettings();
  const save = useSaveSettings();
  const [form, setForm] = useState({});
  useEffect(() => { if (q.data) setForm(q.data); }, [q.data]);

  const testSend = useMutation({
    mutationFn: async () => (await api.post("/admin/newsletter/system-mail/test-send")).data,
    onSuccess: (d) => toast.success(`System-Testmail an ${d?.to} verschickt`),
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  if (q.isLoading) return <div className="text-sm text-zinc-500">Lade…</div>;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="system-mail-panel">
      <section className="border border-zinc-800 rounded-sm bg-zinc-900 p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={16} className="text-cyan-400" />
          <div className="font-black uppercase tracking-wide">System-Mails</div>
        </div>
        <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
          Verifikation, Passwort-Reset und andere Pflicht-Mails. Marketing-Consent
          beeinflusst diese Mails niemals. SMTP-Zugangsdaten bleiben ausschließlich
          in der Server-Umgebung (ENV).
        </p>
        <FieldRow label="Absender-Name" tid="sys-from-name"
          value={form.system_sender_name}
          onChange={(v) => setForm({ ...form, system_sender_name: v })}
          placeholder={q.data?.system_sender_name_effective} />
        <FieldRow label="Absender-E-Mail" tid="sys-from-email"
          value={form.system_sender_email}
          onChange={(v) => setForm({ ...form, system_sender_email: v })}
          placeholder={q.data?.system_sender_email_effective} />
        <FieldRow label="Reply-To" tid="sys-reply"
          value={form.system_reply_to}
          onChange={(v) => setForm({ ...form, system_reply_to: v })}
          placeholder="(optional)" />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className={`text-[11px] px-2 py-1 rounded-sm border ${q.data?.smtp_configured ? "bg-green-500/10 border-green-500/30 text-green-300" : "bg-amber-500/10 border-amber-500/30 text-amber-300"}`}>
            SMTP-Status: {q.data?.smtp_configured ? "konfiguriert" : "nicht in ENV konfiguriert (Dev-Mailbox aktiv)"}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <Button data-testid="sys-save" onClick={() => save.mutate({
            system_sender_name: form.system_sender_name || null,
            system_sender_email: form.system_sender_email || null,
            system_reply_to: form.system_reply_to || null,
            marketing_sender_name: form.marketing_sender_name || null,
            marketing_sender_email: form.marketing_sender_email || null,
            marketing_reply_to: form.marketing_reply_to || null,
            marketing_footer_text: form.marketing_footer_text || null,
          })} disabled={save.isPending} className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950">
            <Save size={14} className="mr-2" /> Speichern
          </Button>
          <Button data-testid="sys-test-send" variant="outline" onClick={() => testSend.mutate()} disabled={testSend.isPending} className="border-zinc-800 rounded-sm">
            <Send size={14} className="mr-2" /> Testmail senden
          </Button>
        </div>
      </section>
    </div>
  );
}

// ==========================================================================
// MarketingSenderPanel — Newsletter-Absender + Footer
// ==========================================================================
export function MarketingSenderPanel() {
  const q = useSettings();
  const save = useSaveSettings();
  const [form, setForm] = useState({});
  useEffect(() => { if (q.data) setForm(q.data); }, [q.data]);

  if (q.isLoading) return null;

  return (
    <section className="border border-zinc-800 rounded-sm bg-zinc-900 p-4 sm:p-5" data-testid="marketing-sender-panel">
      <div className="flex items-center gap-2 mb-3">
        <Mail size={16} className="text-cyan-400" />
        <div className="font-black uppercase tracking-wide">Newsletter-Absender</div>
      </div>
      <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
        Gilt global für alle Newsletter-Kampagnen. Bleibt ein Feld leer, wird der
        System-Wert benutzt. Jeder Newsletter enthält einen un-entfernbaren
        Unsubscribe-Link im Footer.
      </p>
      <FieldRow label="Absender-Name" tid="mk-from-name"
        value={form.marketing_sender_name}
        onChange={(v) => setForm({ ...form, marketing_sender_name: v })}
        placeholder={q.data?.marketing_sender_name_effective} />
      <FieldRow label="Absender-E-Mail" tid="mk-from-email"
        value={form.marketing_sender_email}
        onChange={(v) => setForm({ ...form, marketing_sender_email: v })}
        placeholder={q.data?.marketing_sender_email_effective} />
      <FieldRow label="Reply-To" tid="mk-reply"
        value={form.marketing_reply_to}
        onChange={(v) => setForm({ ...form, marketing_reply_to: v })}
        placeholder="(optional)" />
      <div className="mt-3">
        <Label className="text-xs uppercase tracking-wider text-zinc-400">Footer-Zusatz (optional)</Label>
        <Textarea data-testid="mk-footer" rows={2}
          value={form.marketing_footer_text || ""}
          onChange={(e) => setForm({ ...form, marketing_footer_text: e.target.value })}
          className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm text-sm"
          placeholder="z. B. Anschrift oder rechtlicher Hinweis" />
      </div>
      <div className="flex justify-end mt-4">
        <Button data-testid="mk-save" onClick={() => save.mutate({
          system_sender_name: form.system_sender_name || null,
          system_sender_email: form.system_sender_email || null,
          system_reply_to: form.system_reply_to || null,
          marketing_sender_name: form.marketing_sender_name || null,
          marketing_sender_email: form.marketing_sender_email || null,
          marketing_reply_to: form.marketing_reply_to || null,
          marketing_footer_text: form.marketing_footer_text || null,
        })} disabled={save.isPending} className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950">
          <Save size={14} className="mr-2" /> Absender speichern
        </Button>
      </div>
    </section>
  );
}

// ==========================================================================
// CampaignsPanel — CRUD + Editor + Preview + Test-Send
// ==========================================================================
const CAMPAIGN_EMPTY = {
  title: "", subject: "", body_html: "",
  audience: "all", minimum_age: 0,
  schedule_kind: "now", schedule_at: null,
  status: "draft",
};

export function CampaignsPanel() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewCampaignId, setPreviewCampaignId] = useState(null);
  const [liveSingleCampaign, setLiveSingleCampaign] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(CAMPAIGN_EMPTY);

  const list = useQuery({
    queryKey: ["nl-campaigns"],
    queryFn: async () => (await api.get("/admin/newsletter/campaigns")).data,
  });

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form };
      if (!body.schedule_at) body.schedule_at = null;
      if (editing) return (await api.put(`/admin/newsletter/campaigns/${editing.id}`, body)).data;
      return (await api.post("/admin/newsletter/campaigns", body)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Kampagne aktualisiert" : "Kampagne angelegt");
      setOpen(false); setEditing(null); setForm(CAMPAIGN_EMPTY);
      qc.invalidateQueries({ queryKey: ["nl-campaigns"] });
      qc.invalidateQueries({ queryKey: ["nl-runs-all"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/newsletter/campaigns/${id}`)).data,
    onSuccess: () => {
      toast.success("Kampagne gelöscht");
      qc.invalidateQueries({ queryKey: ["nl-campaigns"] });
    },
    onError: () => toast.error("Löschen fehlgeschlagen"),
  });

  const testSend = useMutation({
    mutationFn: async (id) => (await api.post("/admin/newsletter/test-send", { campaign_id: id })).data,
    onSuccess: (data) => toast.success(`Test-Newsletter an ${data?.to} geschickt`),
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });

  const openNew = () => { setEditing(null); setForm(CAMPAIGN_EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      title: c.title || "", subject: c.subject || "",
      body_html: c.body_html || "",
      audience: c.audience || "all",
      minimum_age: Number(c.minimum_age ?? 0),
      schedule_kind: c.schedule_kind || "now",
      schedule_at: c.schedule_at ? c.schedule_at.slice(0, 16) : null,
      status: c.status || "draft",
    });
    setOpen(true);
  };

  return (
    <div className="space-y-4" data-testid="campaigns-panel">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="text-sm text-zinc-400">{list.data?.length ?? 0} Kampagne(n)</div>
        <Button data-testid="nl-campaign-new" onClick={openNew} className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950">
          <Plus size={14} className="mr-2" /> Neuer Newsletter
        </Button>
      </div>

      <div className="border border-zinc-800 rounded-sm bg-zinc-900 divide-y divide-zinc-800">
        {(list.data || []).map((c) => (
          <div key={c.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`nl-campaign-row-${c.id}`}>
            <div className="flex-1 min-w-[220px]">
              <div className="font-semibold text-sm">{c.title}</div>
              <div className="text-xs text-zinc-500 mt-0.5 truncate">{c.subject}</div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                <StatusBadge status={c.status} />
                <span className="px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300">
                  Zielgruppe: {ageLabel(c.minimum_age, c.audience)}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300">
                  Termin: {scheduleLabel(c)}
                </span>
                {c.runs_count > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300">
                    {c.runs_count} Läufe · zuletzt: {c.last_run_status || "–"}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              <Button size="sm" variant="outline" className="border-zinc-800 rounded-sm" data-testid={`nl-campaign-preview-${c.id}`}
                onClick={() => { setPreviewCampaignId(c.id); setPreviewOpen(true); }}>
                <Eye size={14} className="mr-1" /> Vorschau
              </Button>
              <Button size="sm" variant="outline" className="border-zinc-800 rounded-sm" data-testid={`nl-campaign-test-${c.id}`}
                onClick={() => testSend.mutate(c.id)} disabled={testSend.isPending}>
                <Send size={14} className="mr-1" /> Test an mich
              </Button>
              <Button size="sm" variant="outline" className="border-amber-700 text-amber-300 hover:bg-amber-500/10 rounded-sm" data-testid={`nl-campaign-live-single-${c.id}`}
                onClick={() => setLiveSingleCampaign(c)}>
                <Zap size={14} className="mr-1" /> Live an 1 Kunden
              </Button>
              <Button size="sm" variant="outline" className="border-zinc-800 rounded-sm" data-testid={`nl-campaign-edit-${c.id}`}
                onClick={() => openEdit(c)}>
                <Pencil size={14} />
              </Button>
              <Button size="sm" variant="outline" className="border-red-900 text-red-400 rounded-sm" data-testid={`nl-campaign-delete-${c.id}`}
                onClick={() => { if (window.confirm(`Newsletter "${c.title}" löschen?`)) del.mutate(c.id); }}>
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
        {list.data?.length === 0 && (
          <div className="p-6 text-center text-sm text-zinc-500" data-testid="nl-campaigns-empty">
            Noch kein Newsletter angelegt.
          </div>
        )}
      </div>

      {/* Editor Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="nl-campaign-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Newsletter bearbeiten" : "Neuer Newsletter"}</DialogTitle>
            <DialogDescription>Marketing-Mail an alle Kunden mit gesetztem Marketing-Consent.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Titel (intern)</Label>
              <Input data-testid="nl-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 h-10 bg-zinc-900 border-zinc-800 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Betreff</Label>
              <Input data-testid="nl-form-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1 h-10 bg-zinc-900 border-zinc-800 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400 block mb-1">Inhalt</Label>
              <NewsletterEditor value={form.body_html} onChange={(html) => setForm((f) => ({ ...f, body_html: html }))} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <AgeSelect value={form.minimum_age}
                  onChange={(v) => setForm({ ...form, minimum_age: v })}
                  testId="nl-form-min-age" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Terminart</Label>
                <select data-testid="nl-form-schedule-kind" value={form.schedule_kind}
                  onChange={(e) => setForm({ ...form, schedule_kind: e.target.value })}
                  className="mt-1 w-full h-10 bg-zinc-900 border border-zinc-800 rounded-sm text-sm text-zinc-100 px-2">
                  <option value="now">Sofort senden</option>
                  <option value="once">Einmalig zu Datum/Uhrzeit</option>
                  <option value="weekly">Wöchentlich (ab Datum/Uhrzeit)</option>
                  <option value="monthly">Monatlich (ab Datum/Uhrzeit)</option>
                </select>
              </div>
            </div>
            {form.schedule_kind !== "now" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Anker-Datum/Uhrzeit</Label>
                <Input type="datetime-local" data-testid="nl-form-schedule-at"
                  value={form.schedule_at || ""}
                  onChange={(e) => setForm({ ...form, schedule_at: e.target.value })}
                  className="mt-1 h-10 bg-zinc-900 border-zinc-800 rounded-sm" />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Status</Label>
              <select data-testid="nl-form-status" value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="mt-1 w-full h-10 bg-zinc-900 border border-zinc-800 rounded-sm text-sm text-zinc-100 px-2">
                <option value="draft">Entwurf (nicht versenden)</option>
                <option value="active">Aktiv (Scheduler arbeitet)</option>
                <option value="paused">Pausiert</option>
                <option value="archived">Archiviert</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-zinc-800 rounded-sm" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button data-testid="nl-form-save" className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950" onClick={() => save.mutate()} disabled={save.isPending}>
              <Save size={14} className="mr-2" /> Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} campaignId={previewCampaignId} />
      <LiveSingleDialog
        campaign={liveSingleCampaign}
        onClose={() => setLiveSingleCampaign(null)}
        onSent={() => qc.invalidateQueries({ queryKey: ["nl-runs-all"] })}
      />
    </div>
  );
}

function scheduleLabel(c) {
  const at = c.schedule_at ? new Date(c.schedule_at) : null;
  const at_s = at ? at.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "–";
  if (c.schedule_kind === "now") return "Sofort";
  if (c.schedule_kind === "once") return `Einmalig · ${at_s}`;
  if (c.schedule_kind === "weekly") return `Wöchentlich ab ${at_s}`;
  if (c.schedule_kind === "monthly") return `Monatlich ab ${at_s}`;
  return c.schedule_kind;
}

function ageLabel(minimum_age, audience) {
  // Kanonisch: minimum_age. Alt-Kampagnen aus 0018 hatten nur audience —
  // Backfill in 0019 setzt minimum_age=18 für 'adults_18plus'. Robust
  // gegen ältere Daten: falls minimum_age=0 aber audience=adults_18plus,
  // fallback auf „Ab 18".
  const m = Number(minimum_age ?? 0);
  if (m === 0 && audience === "adults_18plus") return "Ab 18";
  const opt = AGE_OPTIONS.find((o) => o.value === m);
  return opt ? opt.label : "Alle";
}


function StatusBadge({ status }) {
  const map = {
    draft: { label: "Entwurf", cls: "bg-zinc-800 text-zinc-300" },
    active: { label: "Aktiv", cls: "bg-green-500/15 text-green-400 border border-green-500/30" },
    paused: { label: "Pausiert", cls: "bg-amber-500/15 text-amber-400" },
    archived: { label: "Archiviert", cls: "bg-zinc-800 text-zinc-500" },
    pending: { label: "Ausstehend", cls: "bg-amber-500/15 text-amber-400" },
    processing: { label: "Läuft", cls: "bg-cyan-500/15 text-cyan-300" },
    sent: { label: "Gesendet", cls: "bg-green-500/15 text-green-400" },
    failed: { label: "Fehler", cls: "bg-red-500/15 text-red-400" },
    cancelled: { label: "Abgebrochen", cls: "bg-zinc-800 text-zinc-500" },
  };
  const m = map[status] || { label: status, cls: "bg-zinc-800 text-zinc-300" };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${m.cls}`}>{m.label}</span>;
}

function PreviewDialog({ open, onOpenChange, campaignId }) {
  const q = useQuery({
    queryKey: ["nl-preview", campaignId],
    enabled: !!campaignId && open,
    queryFn: async () => (await api.get(`/admin/newsletter/campaigns/${campaignId}/preview`)).data,
  });
  const [mobile, setMobile] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="nl-preview-dialog">
        <DialogHeader>
          <DialogTitle>Vorschau</DialogTitle>
          <DialogDescription>So sähe die Mail für Kundin/Kunden „Max" aus. Rechts kannst du zwischen Desktop/Mobile umschalten.</DialogDescription>
        </DialogHeader>
        {q.isLoading && <div className="text-sm text-zinc-500">Lade Vorschau…</div>}
        {q.data && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap text-xs">
              <div className="text-zinc-500">
                <span className="uppercase tracking-wider">Zielgruppen-Größe: </span>
                <span className="text-cyan-400 font-semibold">{q.data.audience_size}</span>
              </div>
              <div className="ml-auto inline-flex rounded-sm border border-zinc-800 overflow-hidden">
                <button data-testid="nl-preview-desktop" onClick={() => setMobile(false)} className={`px-2 py-1 text-[11px] uppercase tracking-wider ${!mobile ? "bg-cyan-500/20 text-cyan-300" : "text-zinc-400"}`}>Desktop</button>
                <button data-testid="nl-preview-mobile" onClick={() => setMobile(true)} className={`px-2 py-1 text-[11px] uppercase tracking-wider ${mobile ? "bg-cyan-500/20 text-cyan-300" : "text-zinc-400"}`}>Mobile</button>
              </div>
            </div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">Betreff</div>
            <div className="p-2 bg-zinc-900 border border-zinc-800 rounded-sm text-sm">{q.data.subject}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">HTML-Vorschau</div>
            <div className="flex justify-center bg-zinc-800 p-3 rounded-sm">
              <iframe title="Newsletter Preview" data-testid="nl-preview-iframe" sandbox=""
                srcDoc={q.data.body_html}
                style={{ width: mobile ? 375 : "100%", maxWidth: "100%" }}
                className="h-[520px] bg-white border border-zinc-700 rounded-sm" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-800 rounded-sm">Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==========================================================================
// LiveSingleDialog — QA-Live-Send an genau EINEN Kunden
// ==========================================================================
function LiveSingleDialog({ campaign, onClose, onSent }) {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const open = !!campaign;

  useEffect(() => {
    if (!open) { setQ(""); setSelected(null); setConfirming(false); }
  }, [open]);

  // Debounced Suche: das Backend beantwortet 2+ Zeichen ILIKE-Match.
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const search = useQuery({
    enabled: open && debounced.length >= 2,
    queryKey: ["nl-cust-search", debounced],
    queryFn: async () => (await api.get("/admin/newsletter/customers/search",
      { params: { q: debounced, limit: 20 } })).data,
  });

  const send = useMutation({
    mutationFn: async () => (await api.post(
      `/admin/newsletter/campaigns/${campaign.id}/live-send-single`,
      { customer_account_id: selected.account_id },
    )).data,
    onSuccess: (data) => {
      if (data?.sent === 1) {
        toast.success(`Live-Newsletter an ${data.email} zugestellt.`);
      } else {
        toast.error(`Zustellung fehlgeschlagen: ${data?.error || "unbekannt"}`);
      }
      onSent?.();
      onClose();
    },
    onError: (e) => {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Live-Send fehlgeschlagen");
    },
  });

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="nl-live-single-dialog"
        className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>Live-Newsletter an einen Kunden</DialogTitle>
          <DialogDescription>
            Sendet die Kampagne <span className="font-mono text-cyan-400">{campaign.title}</span>{" "}
            als ECHTEN Newsletter an genau einen ausgewählten Kunden. Alle
            Eligibility-Regeln (Consent, verifizierte Mail, Alter) werden
            strikt geprüft.
          </DialogDescription>
        </DialogHeader>

        <div className="border border-amber-700/40 bg-amber-500/5 text-amber-200 text-xs p-3 rounded-sm">
          Achtung: Dies ist KEIN Test-Send. Es entsteht ein echter Run mit einer
          echten Delivery und einem regulären Unsubscribe-Link.
        </div>

        <div className="space-y-2 mt-2">
          <Label className="text-xs uppercase tracking-wider text-zinc-400">Kunde suchen (Name oder E-Mail)</Label>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              data-testid="nl-live-single-search"
              value={q}
              onChange={(e) => { setQ(e.target.value); setSelected(null); }}
              placeholder="z. B. anna.muster oder anna@…"
              className="pl-7 h-9 bg-zinc-900 border-zinc-800 rounded-sm"
              autoFocus
            />
          </div>

          {debounced.length >= 2 && (
            <div
              data-testid="nl-live-single-results"
              className="border border-zinc-800 rounded-sm bg-zinc-900 max-h-[260px] overflow-y-auto divide-y divide-zinc-800"
            >
              {search.isLoading && (
                <div className="p-3 text-xs text-zinc-500">Lade Ergebnisse…</div>
              )}
              {!search.isLoading && (search.data?.length ?? 0) === 0 && (
                <div className="p-3 text-xs text-zinc-500" data-testid="nl-live-single-empty">
                  Keine Treffer.
                </div>
              )}
              {(search.data || []).map((c) => {
                const isSel = selected?.account_id === c.account_id;
                return (
                  <button
                    key={c.account_id}
                    type="button"
                    onClick={() => setSelected(c)}
                    data-testid={`nl-live-single-hit-${c.account_id}`}
                    className={`w-full text-left px-3 py-2 flex items-start justify-between gap-3 text-xs hover:bg-zinc-800/60 ${
                      isSel ? "bg-cyan-500/10" : ""
                    }`}
                  >
                    <div>
                      <div className="text-zinc-200 font-medium">
                        {c.first_name} {c.last_name}
                      </div>
                      <div className="text-zinc-500">{c.email}</div>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 text-[10px]">
                      <span className={c.marketing_opt_in ? "text-green-400" : "text-amber-400"}>
                        {c.marketing_opt_in ? "Consent ✓" : "Kein Consent"}
                      </span>
                      <span className={c.email_verified ? "text-green-400" : "text-amber-400"}>
                        {c.email_verified ? "verifiziert ✓" : "unverifiziert"}
                      </span>
                      <span className="text-zinc-500">
                        DoB: {c.date_of_birth || "–"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {selected && (
            <div
              data-testid="nl-live-single-selected"
              className="border border-cyan-500/40 bg-cyan-500/5 rounded-sm p-3 text-xs mt-2"
            >
              <div className="text-cyan-300 uppercase tracking-wider text-[10px] mb-1">Ausgewählt</div>
              <div className="text-zinc-200">
                {selected.first_name} {selected.last_name}{" "}
                <span className="text-zinc-500">({selected.email})</span>
              </div>
              {!selected.marketing_opt_in && (
                <div className="mt-1 text-amber-300">
                  Achtung: dieser Kunde hat kein Marketing-Consent. Der
                  Server wird den Versand mit 422 ablehnen.
                </div>
              )}
              {!selected.email_verified && (
                <div className="mt-1 text-amber-300">
                  Achtung: E-Mail nicht verifiziert. Der Server wird 422 antworten.
                </div>
              )}
              {campaign.minimum_age > 0 && !selected.date_of_birth && (
                <div className="mt-1 text-amber-300">
                  Achtung: kein Geburtsdatum hinterlegt — bei Alterseinschränkung
                  wird fail-closed abgelehnt.
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-800 rounded-sm">
            Abbrechen
          </Button>
          {!confirming ? (
            <Button
              data-testid="nl-live-single-next"
              disabled={!selected}
              onClick={() => setConfirming(true)}
              className="bg-amber-500 hover:bg-amber-400 text-zinc-950"
            >
              <Zap size={14} className="mr-2" /> Weiter zur Bestätigung
            </Button>
          ) : (
            <Button
              data-testid="nl-live-single-confirm"
              disabled={!selected || send.isPending}
              onClick={() => send.mutate()}
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              {send.isPending ? "Sende…" : "Ja, jetzt LIVE senden"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==========================================================================
// MailHistoryPanel — alle Runs (Empfänger/sent/failed/Status)
// ==========================================================================
export function MailHistoryPanel() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["nl-runs-all"],
    queryFn: async () => (await api.get("/admin/newsletter/runs")).data,
  });
  const campaigns = useQuery({
    queryKey: ["nl-campaigns"],
    queryFn: async () => (await api.get("/admin/newsletter/campaigns")).data,
  });
  const campaignById = React.useMemo(() => {
    const m = {}; (campaigns.data || []).forEach((c) => { m[c.id] = c; }); return m;
  }, [campaigns.data]);

  return (
    <div className="space-y-3" data-testid="mail-history-panel">
      <div className="flex justify-between items-center">
        <div className="text-sm text-zinc-400">{q.data?.length ?? 0} Läufe</div>
        <Button variant="outline" size="sm"
          onClick={() => qc.invalidateQueries({ queryKey: ["nl-runs-all"] })}
          className="border-zinc-800 rounded-sm">
          <RefreshCw size={14} className="mr-1" /> Aktualisieren
        </Button>
      </div>
      <div className="border border-zinc-800 rounded-sm bg-zinc-900 divide-y divide-zinc-800">
        {(q.data || []).map((r) => {
          const c = campaignById[r.campaign_id];
          return (
            <div key={r.id} className="p-3 grid grid-cols-2 sm:grid-cols-6 gap-2 text-xs" data-testid={`nl-run-${r.id}`}>
              <div className="col-span-2">
                <div className="text-zinc-500 uppercase tracking-wider text-[10px]">Kampagne</div>
                <div className="text-zinc-200 truncate">{c?.title || "–"}</div>
                <div className="text-zinc-500 text-[10px]">{ageLabel(c?.minimum_age, c?.audience)}</div>
              </div>
              <div>
                <div className="text-zinc-500 uppercase tracking-wider text-[10px]">Zeitpunkt</div>
                <div className="text-zinc-200">{new Date(r.planned_at).toLocaleString("de-DE")}</div>
              </div>
              <div>
                <div className="text-zinc-500 uppercase tracking-wider text-[10px]">Status</div>
                <div><StatusBadge status={r.status} /></div>
                {r.error && <div className="text-red-400 text-[10px] mt-1 truncate" title={r.error}>{r.error}</div>}
              </div>
              <div>
                <div className="text-zinc-500 uppercase tracking-wider text-[10px]">Empfänger</div>
                <div className="text-zinc-200">{r.stats_recipients}</div>
              </div>
              <div>
                <div className="text-zinc-500 uppercase tracking-wider text-[10px]">Erfolgreich</div>
                <div className="text-green-400">{r.stats_sent}</div>
                {r.stats_failed > 0 && <div className="text-red-400 text-[10px]">Fehler: {r.stats_failed}</div>}
              </div>
            </div>
          );
        })}
        {q.data?.length === 0 && (
          <div className="p-6 text-center text-sm text-zinc-500" data-testid="nl-runs-empty">Noch keine Läufe.</div>
        )}
      </div>
      <div className="text-[11px] text-zinc-500">
        System-Testmails erscheinen hier NICHT — nur echte Newsletter-Versandläufe.
      </div>
    </div>
  );
}

export default {};
